/**
 * SALUS 2.0 nav usability — desktop hover bridge / one-at-a-time / keyboard,
 * plus mobile Escape + is-menu-open sync on html/body.
 * Additive to theme.js. Branding #179095. Menu handle untouched.
 * 2026-09-05: Escape force-close (visual) while keeping trigger focus usable.
 */
(() => {
  const CLOSE_DELAY_MS = 175;
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Desktop dropdown / mega ---------- */
  const items = () => qsa('.nav-desktop__item--has-children');
  let lastTrigger = null;
  const leaveTimers = new WeakMap();

  const panelOf = (item) =>
    item.querySelector(':scope > .nav-desktop__mega, :scope > .nav-desktop__dropdown');

  const triggerOf = (item) => {
    let t = item.querySelector(':scope > [data-nav-trigger]');
    if (!t) t = item.querySelector(':scope > a.nav-desktop__link, :scope > a');
    return t;
  };

  const clearForceClosed = (item) => {
    if (item) item.classList.remove('is-force-closed');
  };

  const closeItem = (item, { forceClosed = false } = {}) => {
    if (!item) return;
    item.classList.remove('is-open');
    if (forceClosed) item.classList.add('is-force-closed');
    else item.classList.remove('is-force-closed');
    const trigger = triggerOf(item);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  };

  const closeAll = (except, opts = {}) => {
    items().forEach((item) => {
      if (except && item === except) return;
      const t = leaveTimers.get(item);
      if (t) {
        clearTimeout(t);
        leaveTimers.delete(item);
      }
      closeItem(item, opts);
    });
  };

  const openItem = (item, trigger) => {
    if (!item) return;
    const t = leaveTimers.get(item);
    if (t) {
      clearTimeout(t);
      leaveTimers.delete(item);
    }
    clearForceClosed(item);
    closeAll(item);
    item.classList.add('is-open');
    const trig = trigger || triggerOf(item);
    if (trig) {
      trig.setAttribute('aria-expanded', 'true');
      lastTrigger = trig;
    }
  };

  const initDesktopItem = (item) => {
    const trigger = triggerOf(item);
    if (!trigger) return;
    trigger.setAttribute('data-nav-trigger', '');
    if (!trigger.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    if (!trigger.hasAttribute('aria-haspopup')) {
      trigger.setAttribute('aria-haspopup', 'true');
    }

    item.addEventListener('mouseenter', () => openItem(item, trigger));
    item.addEventListener('mouseleave', () => {
      const timer = setTimeout(() => closeItem(item), CLOSE_DELAY_MS);
      leaveTimers.set(item, timer);
    });

    item.addEventListener('focusin', (event) => {
      if (!item.contains(event.target)) return;
      // After Escape, trigger stays focused but panel must remain visually closed
      // until intentional reopen (hover / ArrowDown / Enter).
      if (item.classList.contains('is-force-closed')) return;
      openItem(item, trigger);
    });

    trigger.addEventListener('keydown', (event) => {
      const key = event.key;
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const visuallyOpen =
        expanded ||
        item.classList.contains('is-open') ||
        (!item.classList.contains('is-force-closed') &&
          (item.matches(':focus-within') || item.matches(':hover')));

      if (key === 'Escape') {
        if (!visuallyOpen && !item.classList.contains('is-open') && !expanded) return;
        event.preventDefault();
        event.stopPropagation();
        closeAll(null, { forceClosed: true });
        // Keep keyboard focus on the trigger; CSS is-force-closed hides the panel.
        trigger.focus();
        lastTrigger = trigger;
        return;
      }

      if (key === 'ArrowDown') {
        event.preventDefault();
        openItem(item, trigger);
        const panel = panelOf(item);
        const first = panel && panel.querySelector('a[href]');
        if (first) first.focus();
        return;
      }

      if (key === 'Enter' || key === ' ') {
        // First Enter/Space when closed: open without navigating (SHOP may be /collections/all).
        if (!expanded || item.classList.contains('is-force-closed')) {
          event.preventDefault();
          openItem(item, trigger);
          return;
        }
        // Second Enter on open link: allow default navigation.
        if (key === ' ') {
          // Space on open: keep open / prevent page scroll; click navigates separately.
          event.preventDefault();
        }
      }
    });
  };

  const initDesktop = () => {
    items().forEach(initDesktopItem);

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = qs('.nav-desktop__item--has-children.is-open');
      const forceCandidates = qsa('.nav-desktop__item--has-children').filter(
        (el) =>
          el.classList.contains('is-open') ||
          (!el.classList.contains('is-force-closed') &&
            el.querySelector(':scope > .nav-desktop__mega, :scope > .nav-desktop__dropdown') &&
            getComputedStyle(
              el.querySelector(':scope > .nav-desktop__mega, :scope > .nav-desktop__dropdown')
            ).display !== 'none')
      );
      if (!open && forceCandidates.length === 0) return;
      // Prefer desktop close when a desktop panel is open.
      event.preventDefault();
      const focusTarget = lastTrigger || (open && triggerOf(open)) || (forceCandidates[0] && triggerOf(forceCandidates[0]));
      closeAll(null, { forceClosed: true });
      if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
    });

    // Click outside closes desktop panels.
    document.addEventListener('click', (event) => {
      if (event.target.closest('.nav-desktop__item--has-children')) return;
      closeAll();
    });
  };

  /* ---------- Mobile menu helpers (additive to theme.js) ---------- */
  const menuButton = () => qs('.site-header__icon--menu.js-menu-toggle, button.js-menu-toggle[aria-controls="MobileNav"]');

  const syncMenuOpenClass = (open) => {
    const header = qs('[data-header]');
    document.documentElement.classList.toggle('is-menu-open', open);
    document.body.classList.toggle('is-menu-open', open);
    if (header) header.classList.toggle('is-menu-open', open);
  };

  const setMobileMenu = (open) => {
    const header = qs('[data-header]');
    const mobileNav = qs('#MobileNav');
    const overlay = qs('.nav-overlay');
    if (!header || !mobileNav) return;
    syncMenuOpenClass(open);
    mobileNav.hidden = !open;
    if (overlay) {
      overlay.hidden = !open;
      if (open) overlay.removeAttribute('hidden');
      else overlay.setAttribute('hidden', '');
    }
    qsa('.js-menu-toggle').forEach((el) => {
      if (el.tagName === 'BUTTON' || el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', String(open));
      }
    });
    document.body.style.overflow = open ? 'hidden' : '';
  };

  const isMobileMenuOpen = () => {
    const header = qs('[data-header]');
    const mobileNav = qs('#MobileNav');
    return Boolean(
      (header && header.classList.contains('is-menu-open')) ||
        (mobileNav && !mobileNav.hidden)
    );
  };

  const initMobile = () => {
    // Keep html/body in sync when theme.js toggles header class / overlay.
    const header = qs('[data-header]');
    if (header) {
      const obs = new MutationObserver(() => {
        syncMenuOpenClass(header.classList.contains('is-menu-open'));
      });
      obs.observe(header, { attributes: true, attributeFilter: ['class'] });
      syncMenuOpenClass(header.classList.contains('is-menu-open'));
    }

    // Escape closes drawer and returns focus to menu button.
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!isMobileMenuOpen()) return;
      // If a desktop panel is open, the desktop handler above already ran; skip if desktop.
      if (qs('.nav-desktop__item--has-children.is-open')) return;
      event.preventDefault();
      setMobileMenu(false);
      const btn = menuButton();
      if (btn) btn.focus();
    });
  };

  const boot = () => {
    initDesktop();
    initMobile();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
